import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import API_BASE_URL from '../config/api';
import '../styles/LoveNotes.css';

const LoveNotes = ({ user }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateNote, setShowCreateNote] = useState(false);
  const [newNote, setNewNote] = useState({
    message: '',
    scheduledDate: '',
    sender: 'With all my love ❤️'
  });

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotes(data);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNote = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newNote)
      });

      if (response.ok) {
        setShowCreateNote(false);
        setNewNote({
          message: '',
          scheduledDate: '',
          sender: 'With all my love ❤️'
        });
        fetchNotes();
        alert('Love note created successfully! 💕');
      } else {
        const errData = await response.json();
        alert(`Error: ${errData.message || 'Unable to create note'}`);
      }
    } catch (error) {
      console.error('Error creating note:', error);
      alert('Error creating note. Please try again.');
    }
  };

  const revealNote = async (noteId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}/reveal`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchNotes();
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Unable to reveal note'}`);
      }
    } catch (error) {
      console.error('Error revealing note:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this love note?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        fetchNotes();
        alert('Love note deleted successfully! 💔');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Unable to delete note'}`);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note. Please try again.');
    }
  };

  const todayNotes = notes.filter(note => note.isToday);
  const upcomingNotes = notes.filter(note => !note.isToday && !note.isRevealed);
  const revealedNotes = notes.filter(note => note.isRevealed && !note.isToday);

  if (loading) {
    return (
      <div className="love-notes">
        <div className="loading">Loading love notes... 💕</div>
        <Navigation />
      </div>
    );
  }

  if (showCreateNote) {
    return (
      <div className="love-notes">
        <div className="container">
          <div className="page-header">
            <h1>Create Love Note</h1>
            <p>Write a sweet message for your partner</p>
          </div>

          <div className="create-note-form">
            <div className="form-group">
              <label>Love Message</label>
              <textarea
                placeholder="Write your sweet message here... 💕"
                value={newNote.message}
                onChange={(e) => setNewNote({ ...newNote, message: e.target.value })}
                className="message-textarea"
                rows="4"
              />
            </div>

            <div className="form-group">
              <label>Schedule For (Optional)</label>
              <input
                type="date"
                value={newNote.scheduledDate}
                onChange={(e) => setNewNote({ ...newNote, scheduledDate: e.target.value })}
                className="date-input"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label>Signature</label>
              <input
                type="text"
                placeholder="With all my love ❤️"
                value={newNote.sender}
                onChange={(e) => setNewNote({ ...newNote, sender: e.target.value })}
                className="sender-input"
              />
            </div>

            <div className="form-actions">
              <button onClick={createNote} className="btn btn-primary" disabled={!newNote.message}>
                💌 Send Love Note
              </button>
              <button onClick={() => setShowCreateNote(false)} className="btn btn-outline">
                Cancel
              </button>
            </div>
          </div>
        </div>
        <Navigation />
      </div>
    );
  }

  const renderNoteCard = (note, type) => (
    <div key={note._id} className={`note-card ${type}`}>
      <div className="note-content">
        {type === 'upcoming' ? (
          <div className="note-preview">
            <p className="note-message blurred">
              {note.message.substring(0, 20)}...
            </p>
            <button
              onClick={() => revealNote(note._id)}
              className="reveal-btn"
            >
              💌 Reveal Message
            </button>
          </div>
        ) : (
          <>
            <div className="heart-decoration">💖</div>
            <p className="note-message">{note.message}</p>
            <p className="note-sender">{note.sender}</p>
          </>
        )}
        <p className="note-author">From: {note.createdBy?.name || note.createdBy || 'Unknown'}</p>
        {note.scheduledDate && (
          <p className="note-date">
            {type === 'upcoming' ? 
              `Scheduled for: ${new Date(note.scheduledDate).toLocaleDateString()}` : 
              `Originally scheduled: ${new Date(note.scheduledDate).toLocaleDateString()}`}
          </p>
        )}
        {note.revealedAt && (
          <p className="note-date">
            Revealed: {new Date(note.revealedAt).toLocaleDateString()}
          </p>
        )}
      </div>
      {(note.createdBy?._id === user?.id || note.createdBy === user?.id) && (
        <button
          onClick={() => handleDeleteNote(note._id)}
          className="btn btn-danger delete-note-btn"
        >
          🗑️
        </button>
      )}
    </div>
  );

  return (
    <div className="love-notes">
      <div className="container">
        <div className="page-header">
          <div className="heart-icon">💕</div>
          <h1>Daily Love Notes</h1>
          <p className="subtitle">✨ Sweet messages from your hearts ✨</p>
        </div>

        <div className="notes-actions">
          <button onClick={() => setShowCreateNote(true)} className="btn btn-primary">
            ✍️ Write Love Note
          </button>
        </div>

        {/* Today's Messages */}
        <div className="notes-section">
          <div className="section-header">
            <h3>📝 Today's Messages</h3>
          </div>
          
          {todayNotes.length > 0 ? (
            todayNotes.map(note => renderNoteCard(note, 'today'))
          ) : (
            <div className="note-card empty">
              <p>No messages for today. Write one for your partner! 💕</p>
            </div>
          )}
        </div>

        {/* Upcoming Messages */}
        <div className="notes-section">
          <div className="section-header">
            <h3>💕 Upcoming Messages</h3>
          </div>
          
          {upcomingNotes.length > 0 ? (
            upcomingNotes.map(note => renderNoteCard(note, 'upcoming'))
          ) : (
            <div className="empty-state">
              <div className="heart-icon">💖</div>
              <p>No upcoming messages. Create some surprise notes!</p>
            </div>
          )}
        </div>

        {/* Previously Revealed Messages */}
        {revealedNotes.length > 0 && (
          <div className="notes-section">
            <div className="section-header">
              <h3>💌 Past Love Notes</h3>
            </div>
            
            {revealedNotes.map(note => renderNoteCard(note, 'revealed'))}
          </div>
        )}
      </div>
      
      <Navigation />
    </div>
  );
};

export default LoveNotes;
